import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type { TournamentPlayerEntryActionResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player WITHDRAW mutation (S4) — DELETE /tournaments/entries/{entry_id} via
 * tournamentApi.withdrawEntry, keyed ONLY by the registration's entry_id (never a
 * fee/amount). MONEY-touching but the hook owns NONE of the money logic: the
 * backend honors the refund within tournament.refund_deadline_at server-side
 * (#1402). The action response CARRIES a server-computed refund_amount, but the
 * FE never surfaces it as money — the confirm shows only the refund POLICY text.
 *
 * On SUCCESS it invalidates the tournament-scoped registrations + eligibility
 * queries (registrationsMine + eligibility) so the per-event list
 * (TournamentEventList) refetches and the withdrawn event's row flips back to
 * "Register" — the flip comes from the SERVER's refetched entries, never an
 * optimistic local guess. A FAILED withdraw invalidates NOTHING (the row stays
 * "Registered"), so a failed/rejected withdraw can never fabricate a not-registered
 * state. Mirrors useRegisterEntry.
 *
 * `tournamentId` is needed ONLY to key the invalidation (the withdraw endpoint
 * itself is entry-scoped). The confirm surface owns the pending/disabled gating
 * off `isPending` for the one-call double-click guard.
 */
export function useWithdrawEntry(tournamentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    TournamentPlayerEntryActionResponse,
    unknown,
    { entryId: string }
  >({
    mutationFn: ({ entryId }) => tournamentApi.withdrawEntry(entryId),
    onSettled: () => queryClient.invalidateQueries({
      queryKey: queryKeys.tournaments.myRegistrationPayments(),
    }),
    onSuccess: async () => {
      const id = tournamentId ?? "";
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.registrationsMine(id),
      });
      // Withdrawing frees a discipline slot, so a previously-blocked event may
      // become eligible again — refetch the verdicts so the list un-greys it.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.eligibility(id),
      });
      // The per-event aggregate entry_count lives on the events-list query, so a
      // successful entry/withdrawal must refetch it too — otherwise the row's
      // "N entries" count goes stale until a hard refresh.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.events(id),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.myRegistrations(),
      });
    },
  });
}
